import type { CameraColumnV2 } from "~/hooks/Camera/v2";

/**
 * Preference order for the grid tile's headline number, most specific first.
 *
 * An org with a bespoke marbling column wants that one — `jbs_wagyu_marbling`
 * is ext-sourced and exists only for JBS Wagyu, whose rows carry no value in
 * any of the shared marbling columns. Otherwise it is the region's headline
 * marbling: MSA for the AUS presets (0–1200, matching the design), USDA for
 * USA/BR, with `aus_marbling` (0–9) and `imf` behind them as last resorts.
 *
 * Resolved from the response's `columns` because the v2 individual-images
 * contract carries no preset id.
 */
const TILE_SCORE_IDS = [
  "jbs_wagyu_marbling",
  "msa_marbling",
  "usda_marbling",
  "aus_marbling",
  "imf",
  "bms_marbling",
] as const;

export const resolveTileScoreColumn = (
  columns: readonly CameraColumnV2[] | undefined,
): CameraColumnV2 | undefined => {
  if (columns === undefined) return undefined;
  for (const id of TILE_SCORE_IDS) {
    const match = columns.find((column) => column.id === id);
    if (match !== undefined) return match;
  }
  return undefined;
};

/**
 * The same preference over bare row keys, for feeds that carry no v2 column
 * metadata (the v1 `QueryResultTable` behind the chart drill-throughs). Keys
 * there are the upper-cased v2 ids, so the match is case-insensitive.
 */
export const resolveTileScoreKey = (
  keys: readonly string[],
): string | undefined => {
  for (const id of TILE_SCORE_IDS) {
    const match = keys.find((key) => key.toLowerCase() === id);
    if (match !== undefined) return match;
  }
  return undefined;
};
